import React from "react";
import { Card, Avatar, Spinner, TextInput } from "flowbite-react";
import { ArrowRight, Search } from "lucide-react";
import { Link} from "react-router-dom";
import AppHeader from "./AppHeader";
import { Therapist } from "../types/therapist";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";

const getInitials = (name: string) => {
  if (!name) return "TT";
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .substring(0, 2)
    .toUpperCase();
};

const Therapists: React.FC = () => {
  const [therapists, setTherapists] = React.useState<Therapist[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [searchQuery, setSearchQuery] = React.useState("");

  React.useEffect(() => {
    const fetchTherapists = async () => {
      try {
        // Verificar cache (válido por 5 minutos)
        const cacheKey = 'therapists_cache';
        const cacheTimeKey = 'therapists_cache_time';
        const cachedData = localStorage.getItem(cacheKey);
        const cacheTime = localStorage.getItem(cacheTimeKey);
        
        const now = Date.now();
        const fiveMinutes = 5 * 60 * 1000;
        
        if (cachedData && cacheTime && (now - parseInt(cacheTime)) < fiveMinutes) {
          // Usar cache
          setTherapists(JSON.parse(cachedData));
          setIsLoading(false);
          return;
        }

        // Buscar do servidor
        const getPublicTherapists = httpsCallable<void, { success: boolean; therapists: Therapist[] }>(
          functions,
          "getPublicTherapists"
        );

        const result = await getPublicTherapists();
        
        if (result.data.success) {
          setTherapists(result.data.therapists);
          // Salvar no cache
          localStorage.setItem(cacheKey, JSON.stringify(result.data.therapists));
          localStorage.setItem(cacheTimeKey, now.toString());
        } else {
          setError("Erro ao carregar terapeutas.");
        }
      } catch (err: any) {
        console.error("Erro ao buscar terapeutas:", err);
        setError(err?.message || "Erro ao carregar terapeutas.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchTherapists();
  }, []);

  const filteredTherapists = React.useMemo(() => {
    const normalizedQuery = searchQuery.toLowerCase();

    if (!normalizedQuery) {
      return therapists;
    }

    return therapists.filter(
      (t) =>
        (t.nomePublico || "").toLowerCase().includes(normalizedQuery) ||
        (t.title || "").toLowerCase().includes(normalizedQuery) ||
        (t.especialidade || "").toLowerCase().includes(normalizedQuery)
    );
  }, [therapists, searchQuery]);

  return (
    <>
      <AppHeader />

      <div className="bg-[#F3F6F1] pt-24 pb-10 sm:pt-28 sm:pb-14">
        <div className="mx-auto flex max-w-6xl flex-col gap-8 px-4">
          <section className="relative overflow-hidden rounded-[2rem] bg-[#2E4B4C] shadow-[0_24px_80px_rgba(46,75,76,0.18)]">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(255,255,255,0.18),_transparent_34%),linear-gradient(135deg,_rgba(244,233,213,0.12),_transparent_55%)]" />
            <div className="relative grid min-h-[340px] items-stretch lg:grid-cols-[1.1fr_0.9fr]">
              <div className="flex flex-col justify-center px-6 py-6 sm:px-10 sm:py-7 lg:px-14 lg:py-9">
                <h1 className="max-w-2xl text-4xl font-semibold leading-tight tracking-tight text-[#F8F5ED] sm:text-5xl lg:text-6xl">
                  Escolha Seu Constelador com Segurança
                </h1>

                <div className="mt-5 max-w-2xl space-y-3 text-sm leading-6 text-[#D9E5E0] sm:text-base sm:leading-7">
                  <p>
                    Acesse profissionais certificados em Constelação Familiar, avaliados na prática e homologados pela plataforma.
                  </p>
                  <p>
                    Entre no perfil de um dos profissionais e faça o seu agendamento Online com um clique, realize o pagamento com segurança, direto pela plataforma.
                  </p>
                  <p>
                    Ou, se preferir: você pode participar de constelações já agendadas e conhecer o profissional antes de escolher o seu, trazendo mais confiança para a sua decisão.
                  </p>
                  <p className="font-semibold text-[#F8F5ED]">
                    Entre no grupo e acompanhe as próximas constelações disponíveis.
                  </p>

                  <a
                    href="https://chat.whatsapp.com/DsJxwW1i4IJ6pernEwmtQC"
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 rounded-full bg-[#F4E9D5] px-5 py-2.5 text-sm font-semibold text-[#2E4B4C] transition-colors hover:bg-[#EAD9BC]"
                  >
                    Entrar no grupo do WhatsApp
                  </a>
                </div>
              </div>

              <div className="relative min-h-[200px] lg:min-h-full">
                <img
                  src="/assets/terapeutas.gif"
                  alt="Constelador conduzindo uma sessão"
                  className="h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#2E4B4C]/55 via-transparent to-transparent" />
              </div>
            </div>
          </section>

          <div className="rounded-[1.5rem] border border-[#D5E1DB] bg-white/90 p-4 shadow-[0_14px_35px_rgba(58,88,89,0.08)] backdrop-blur-sm sm:p-5">
            <div className="mx-auto flex max-w-3xl flex-col gap-3">
              <TextInput
                id="search"
                className="border-[#A56096]"
                placeholder="Buscar por nome, título ou especialidade..."
                required
                icon={Search}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <div className="flex flex-col gap-2 text-sm text-[#496566] sm:flex-row sm:items-center sm:gap-5">
                <span>{therapists.length} {therapists.length === 1 ? "perfil disponível" : "perfis disponíveis"} para consulta</span>
                <span className="hidden h-1 w-1 rounded-full bg-[#85A7A8] sm:block" />
                <span>Busca rápida por nome, título ou especialidade</span>
              </div>
            </div>
          </div>

        {isLoading && (
          <div className="text-center py-12 flex flex-col items-center justify-center">
            <Spinner aria-label="Carregando terapeutas..." size="xl" />
            <p className="mt-2 text-gray-600">Carregando...</p>
          </div>
        )}
        {error && <p className="text-center text-red-600 py-12">{error}</p>}

        {!isLoading && !error && (
          <>
            {filteredTherapists.length > 0 ? (
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                {filteredTherapists.map((t) => (
                  <Card
                    key={t.id}
                    className="flex h-full flex-col rounded-[1.75rem] border-0 bg-white text-lg shadow-[0_18px_40px_rgba(58,88,89,0.12)] transition-transform duration-300 hover:-translate-y-1"
                  >
                    <div className="flex flex-col items-center">
                      <Avatar
                        img={t.profilePhotoURL || undefined}
                        placeholderInitials={getInitials(t.nomePublico)}
                        size="lg"
                        rounded
                      />
                      <h5
                        style={{ color: "#6C4E78" }}
                        className="mt-3 mb-1 text-center text-xl font-bold tracking-tight text-white"
                      >
                        {t.nomePublico}
                      </h5>
                      <span
                        style={{ color: "#4A7C75" }}
                        className="text-center text-sm font-bold font-medium text-white/80"
                      >
                        {t.title}
                      </span>
                    </div>
                    <div
                      style={{ color: "#4A7C75" }}
                      className="flex flex-grow flex-col pt-4 font-semibold"
                    >
                      <p className="text-sm text-center line-clamp-4 flex-grow min-h-[80px]">
                        {t.bio}
                      </p>
                      <Link
                        to={`/terapeuta/${t.id}`}
                        className="w-full mt-4 flex items-center justify-center gap-2 bg-[#4A7C75] text-white px-5 py-2 rounded-full font-semibold text-sm hover:bg-[#406b65] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#4A7C75] transition-colors"
                      >
                        Ver mais
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="rounded-[1.75rem] bg-white/80 px-6 py-12 text-center shadow-[0_18px_40px_rgba(58,88,89,0.08)] backdrop-blur-sm">
                <small className="text-sm text-gray-600">
                  Nenhum terapeuta encontrado
                </small>
              </div>
            )}
          </>
        )}
        </div>
      </div>
    </>
  );
};

export { Therapists };
